export type CourseEmbedMode = 'catalogue' | 'course'

/** 'frame' is an iframe that works anywhere. 'script' puts the courses straight into the page, in the page's own fonts. */
export type CourseEmbedStyle = 'frame' | 'script'

export type CourseEmbedOptions = {
  /** The address of the CertSlate app the embed is served from (https://app.certslate.com in production). */
  origin: string
  tenantSlug: string
  mode: CourseEmbedMode
  /** Required when mode is 'course'. */
  courseSlug?: string
  /** Catalogue only: show just this category. */
  category?: string | null
  /** Catalogue only: show just these courses. */
  courses?: string[]
  /** Catalogue only: show at most this many. */
  limit?: number | null
  /** An accent colour as "#rrggbb"; leave out to use the customer's own brand colour. */
  colour?: string | null
  /** In-page script, catalogue only: where each card goes on the customer's own site, with {slug} for the course's web address. */
  linkTemplate?: string | null
}

/** The address the iframe loads. */
export function courseEmbedUrl(o: CourseEmbedOptions): string {
  const params = new URLSearchParams()
  if (o.mode === 'catalogue') {
    if (o.category) params.set('category', o.category)
    if (o.courses && o.courses.length > 0) params.set('courses', o.courses.join(','))
    if (o.limit && o.limit > 0) params.set('limit', String(o.limit))
  }
  // Without the "#", so the address stays easy to read and copy.
  if (o.colour) params.set('colour', o.colour.replace(/^#/, ''))
  const query = params.toString().replace(/%2C/g, ',')

  const path = o.mode === 'course' && o.courseSlug
    ? `/embed/course/${o.tenantSlug}/${o.courseSlug}`
    : `/embed/courses/${o.tenantSlug}`
  return `${o.origin}${path}${query ? `?${query}` : ''}`
}

/**
 * What the customer pastes into their page: the frame, with a height that works without the script, and the loader
 * that makes the frame fit its content.
 */
export function courseEmbedSnippet(o: CourseEmbedOptions, style: CourseEmbedStyle = 'frame'): string {
  if (style === 'script') return courseWidgetSnippet(o)
  const title = o.mode === 'course' ? 'Course details' : 'Training courses'
  return [
    '<iframe',
    // In HTML the "&" between address parts is written "&amp;".
    `  src="${courseEmbedUrl(o).replace(/&/g, '&amp;')}"`,
    `  title="${title}"`,
    '  loading="lazy"',
    '  style="width:100%;height:600px;border:0;display:block"',
    '></iframe>',
    `<script async src="${o.origin}/embed.js"></script>`,
  ].join('\n')
}

function htmlAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

/**
 * The in-page version: the loader script and a custom element. The courses become part of the customer's page, so
 * they take on its fonts, can be themed with CSS, and search engines can read them.
 */
export function courseWidgetSnippet(o: CourseEmbedOptions): string {
  const attributes: [string, string | undefined][] = [['tenant', o.tenantSlug]]
  let tag = 'certslate-courses'
  if (o.mode === 'course' && o.courseSlug) {
    tag = 'certslate-course'
    attributes.push(['course', o.courseSlug])
  } else {
    if (o.category) attributes.push(['category', o.category])
    if (o.courses && o.courses.length > 0) attributes.push(['courses', o.courses.join(',')])
    if (o.limit && o.limit > 0) attributes.push(['limit', String(o.limit)])
    if (o.linkTemplate?.trim()) attributes.push(['link-template', o.linkTemplate.trim()])
  }
  if (o.colour) attributes.push(['colour', o.colour.replace(/^#/, '')])

  const attrs = attributes.map(([name, value]) => `${name}="${htmlAttribute(value ?? '')}"`).join(' ')
  return [
    `<script async src="${o.origin}/certslate-courses.js"></script>`,
    `<${tag} ${attrs}></${tag}>`,
  ].join('\n')
}
